<!--
  HistoryPopover Component
  
  Displays a history icon that shows production logs in a popover when clicked.
  
  Props:
  - logs: Array of production log objects with the following structure:
    {
      pk: number,
      action: string,
      status_values: { old: string, new: string } | null,
      user: { pk: number, username: string },
      created: string
    }
  - position: String - popover position ('bottom-right', 'bottom-left', 'top-right', 'top-left')
-->
<template>
  <div class="history-popover-container">
    <button
      @click="togglePopover"
      class="btn btn-info btn-xs"
      title="Historia zmian"
    >
      <i class="fa fa-history" aria-hidden="true"></i>
    </button>

    <div v-if="showPopover" class="history-popover" :style="popoverStyle">
      <div class="history-popover-header">
        <h5>Historia zmian</h5>
        <button @click="closePopover" class="close-btn">
          <i class="fa fa-times"></i>
        </button>
      </div>

      <div class="history-popover-content">
        <div v-if="!logs || logs.length === 0" class="no-logs">
          Brak danych o zmianach
        </div>

        <div v-else class="logs-list">
          <div v-for="log in logs" :key="log.pk" class="log-item">
            <div class="log-header">
              <span class="log-action">{{ getActionName(log.action) }}</span>
              <span class="log-date">{{ formatDate(log.created) }}</span>
            </div>

            <div class="log-user">
              <i class="fa fa-user"></i>
              {{ log.user.username }}
            </div>

            <div v-if="log.status_values" class="log-status-changes">
              <div class="status-change">
                <span class="status-label">Status:</span>
                <span class="status-old">{{ log.status_values.old }}</span>
                <i class="fa fa-arrow-right"></i>
                <span class="status-new">{{ log.status_values.new }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from "vue";

// Global state for managing open popovers
const openPopoverId = ref(null);

export default {
  name: "HistoryPopover",
  props: {
    logs: {
      type: Array,
      default: () => [],
    },
    position: {
      type: String,
      default: "bottom-left",
    },
  },
  setup(props) {
    const popoverId = ref(Math.random().toString(36).substr(2, 9));
    const showPopover = computed({
      get: () => openPopoverId.value === popoverId.value,
      set: (value) => {
        if (value) {
          openPopoverId.value = popoverId.value;
        } else {
          openPopoverId.value = null;
        }
      },
    });

    const popoverStyle = computed(() => {
      if (!showPopover.value) return {};

      // Position the popover based on the position prop
      const positions = {
        "bottom-right": {
          top: "100%",
          right: "0",
          left: "auto",
        },
        "bottom-left": {
          top: "100%",
          right: "100%",
          left: "auto",
          marginRight: "10px",
        },
        "top-right": {
          bottom: "100%",
          right: "0",
          left: "auto",
        },
        "top-left": {
          bottom: "100%",
          left: "0",
          right: "auto",
        },
      };

      return {
        ...(positions[props.position] || positions["bottom-left"]),
        position: "absolute",
        zIndex: 9999,
      };
    });

    const togglePopover = () => {
      if (showPopover.value) {
        showPopover.value = false;
      } else {
        showPopover.value = true;
      }
    };

    const closePopover = () => {
      showPopover.value = false;
    };

    const getActionName = (action) => {
      const actionNames = {
        edycja: "Edycja",
        utworzenie: "Utworzenie",
        usunięcie: "Usunięcie",
      };
      return actionNames[action] || action;
    };

    const formatDate = (dateString) => {
      try {
        // Parse the date string and format it
        const date = new Date(
          dateString.replace(
            /(\d{2})\.(\d{2})\.(\d{4}) (\d{2}):(\d{2}):(\d{2})/,
            "$3-$2-$1T$4:$5:$6"
          )
        );
        return date.toLocaleString("pl-PL", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        });
      } catch (error) {
        return dateString;
      }
    };

    // Removed global click listener to prevent scroll blocking
    // Popover will only close via close button click

    return {
      showPopover,
      popoverStyle,
      togglePopover,
      closePopover,
      getActionName,
      formatDate,
    };
  },
};
</script>

<style scoped>
.history-popover-container {
  position: relative;
  display: inline-block;
}

.history-popover {
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 350px;
  max-width: 450px;
  max-height: 400px;
  overflow: hidden;
}

.history-popover-header {
  background: #f8f9fa;
  border-bottom: 1px solid #ddd;
  padding: 12px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.history-popover-header h5 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 0;
  font-size: 14px;
}

.close-btn:hover {
  color: #333;
}

.history-popover-content {
  max-height: 320px;
  overflow-y: auto;
}

.no-logs {
  padding: 20px;
  text-align: center;
  color: #666;
  font-style: italic;
}

.logs-list {
  padding: 0;
}

.log-item {
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
}

.log-item:last-child {
  border-bottom: none;
}

.log-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.log-action {
  font-weight: 600;
  color: #333;
  font-size: 13px;
}

.log-date {
  color: #666;
  font-size: 12px;
}

.log-user {
  display: flex;
  align-items: center;
  color: #555;
  font-size: 12px;
  margin-bottom: 8px;
}

.log-user i {
  margin-right: 6px;
  color: #007bff;
}

.log-status-changes {
  background: #f8f9fa;
  padding: 8px 10px;
  border-radius: 4px;
  border-left: 3px solid #007bff;
}

.status-change {
  display: flex;
  align-items: center;
  font-size: 12px;
}

.status-label {
  font-weight: 600;
  color: #333;
  margin-right: 8px;
}

.status-old {
  background: #ff6b6b;
  color: white;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 11px;
  margin-right: 6px;
}

.status-new {
  background: #51cf66;
  color: white;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 11px;
  margin-left: 6px;
}

.status-change i {
  color: #666;
  font-size: 10px;
}
</style>
